import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Stack,
  Button,
  IconButton,
  Grid,
  CircularProgress,
  Fab,
  InputBase,
  TextField,
  Divider,
  Skeleton,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import {
  Call as CallIcon,
  Person as PersonIcon,
  PhoneInTalk as PhoneInTalkIcon,
  RingVolume as RingVolumeIcon,
  CallEnd as CallEndIcon,
  Remove as MinimizeIcon,
  DragIndicator as DragIcon,
  Backspace as BackspaceIcon,
  Close as CloseIcon,
  Dialpad as DialpadIcon,
  PhoneCallback as PhoneCallbackIcon,
  Save as SaveIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  CallMissed as CallMissedIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useCall } from "../Context/CallContext";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../Config/api";
import { API_URL_CALL } from "../Config/GrandStreamApi/api";
import { SOCKET_URL_CALL } from "../Config/GrandStreamApi/fast_api";

const cleanPhoneNumber = (number) => {
  if (!number) return "";
  return number.toString().replace(/^(?:\+91|0)+/, "");
};

export default function UnifiedSoftphone() {
  const {
    incomingCall,
    isMinimized,
    setIsMinimized,
    showSideDialpad,
    setShowSideDialpad,
    handleRejectLocally, // Added Reject function from context
  } = useCall();

  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const constraintsRef = useRef(null);
  const [callDuration, setCallDuration] = useState(0);
  const [dialedValue, setDialedValue] = useState("");
  const [isCalling, setIsCalling] = useState(false);

  // --- VIEW STATE (RECENTS vs DIALPAD) ---
  const [activeTab, setActiveTab] = useState("RECENTS");

  const AnimatedCount = ({ target }) => {
    const count = useMotionValue(0);
    const rounded = useTransform(count, (latest) => {
      const val = Math.round(latest);
      return val > 99 ? "99+" : val;
    });

    React.useEffect(() => {
      const controls = animate(count, target, {
        duration: 0.8,
        ease: "easeOut",
      });
      return controls.stop;
    }, [target, count]);

    return <motion.span>{rounded}</motion.span>;
  };

  // --- RECENT CALLS & FILTERS STATE ---
  const [recentCalls, setRecentCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callFilter, setCallFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Counts from API
  const [counts, setCounts] = useState({
    total: 0,
    missed: 0,
    incoming: 0,
    outgoing: 0,
  });

  // --- REMARK STATES ---
  const [remark, setRemark] = useState("");
  const [isSavingRemark, setIsSavingRemark] = useState(false);
  const [isLoadingRemark, setIsLoadingRemark] = useState(false);

  const hasActiveCall = incomingCall !== null;
  const isWidgetVisible = showSideDialpad || hasActiveCall;

  // ==========================================
  // --- BULLETPROOF CALL STATUS LOGIC ---
  // ==========================================
  const safeStatus = (incomingCall?.status || "").toUpperCase();
  const myExtension = localStorage.getItem("selected_extension") || "108";
  const uniqueId = incomingCall?.uniqueid;
  const linkedId = incomingCall?.linkedid || incomingCall?.uniqueid;

  const isCloudValue = myExtension === "1111" ? "False" : "True";

  const isOutgoingCallFlow =
    incomingCall?.src === myExtension || safeStatus.includes("OUTGOING");
  const isIncomingCallFlow = !isOutgoingCallFlow;

  const isRinging = safeStatus.includes("RINGING");
  const isCompleted =
    safeStatus.includes("COMPLETED") ||
    safeStatus.includes("HANGUP") ||
    safeStatus.includes("END");
  const isAnswered = hasActiveCall && !isRinging && !isCompleted;

  const rawNumber = isOutgoingCallFlow ? incomingCall?.dst : incomingCall?.src;
  const displayNumber = cleanPhoneNumber(rawNumber);

  // Dynamic Colors
  const themeColor =
    isRinging && isIncomingCallFlow
      ? "#f39c12"
      : isRinging && isOutgoingCallFlow
        ? "#3498db"
        : isAnswered
          ? "#00D2A0"
          : isCompleted
            ? "#e74c3c"
            : "#347B89";

  // ==========================================
  // --- FETCH RECENT CALLS API ---
  // ==========================================
  const fetchRecentCalls = useCallback(async () => {
    setCallsLoading(true);
    try {
      let url = `${API_URL}/web/all-call-details/?extension=${myExtension}`;
      if (callFilter) url += `&type=${callFilter}`;
      if (dateRange.from && dateRange.to) {
        url += `&from_date=${dateRange.from}&to_date=${dateRange.to}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRecentCalls(data.groups || []);

        setCounts({
          total: data.total_count || 0,
          missed: data.total_missed || 0,
          incoming: data.total_incoming || 0,
          outgoing: data.total_outgoing || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching recent calls:", error);
    } finally {
      setCallsLoading(false);
    }
  }, [myExtension, callFilter, dateRange, token]);

  useEffect(() => {
    if (activeTab === "RECENTS" && !isMinimized && isWidgetVisible) {
      fetchRecentCalls();
    }
  }, [
    activeTab,
    isMinimized,
    isWidgetVisible,
    callFilter,
    dateRange,
    fetchRecentCalls,
  ]);

  const filteredGroups = recentCalls
    .map((group) => {
      return {
        ...group,
        calls: group.calls.filter((call) => {
          if (searchQuery) {
            const num = call.phone_number || call.src || call.dst || "";
            if (!num.includes(searchQuery)) return false;
          }

          let computedType = "incoming";
          if (call.direction === "OUTBOUND") {
            computedType = "outgoing";
          } else if (
            call.direction === "INBOUND" &&
            (call.status === "MISSED" ||
              call.status === "REJECTED" ||
              call.status === "RINGING")
          ) {
            computedType = "missed";
          } else {
            computedType = "incoming";
          }

          if (callFilter && callFilter !== computedType) {
            return false;
          }
          return true;
        }),
      };
    })
    .filter((group) => group.calls.length > 0);

  // Call Duration Timer
  useEffect(() => {
    let interval;
    if (isAnswered) {
      interval = setInterval(() => setCallDuration((prev) => prev + 1), 1000);
    } else if (isCompleted || !hasActiveCall) {
      clearInterval(interval);
      if (!hasActiveCall) {
        setCallDuration(0);
        setRemark("");
      }
    }
    return () => clearInterval(interval);
  }, [isAnswered, isCompleted, hasActiveCall]);

  // Fetch Call Remark
  useEffect(() => {
    const fetchCallRemark = async () => {
      if (isAnswered && uniqueId) {
        setIsLoadingRemark(true);
        setTimeout(async () => {
          try {
            const response = await fetch(
              `${API_URL}/web/call-remark/?uniqueid=${uniqueId}&is_cloud=${isCloudValue}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: token ? `Bearer ${token}` : "",
                },
              },
            );
            if (response.ok) {
              const data = await response.json();
              if (data && data.remark) setRemark(data.remark);
            }
          } catch (error) {
            console.error("Error fetching remark:", error);
          } finally {
            setIsLoadingRemark(false);
          }
        }, 1000);
      }
    };
    fetchCallRemark();
  }, [isAnswered, uniqueId, token, isCloudValue]);

  const handleSaveRemark = async () => {
    if (!uniqueId) return;
    setIsSavingRemark(true);

    try {
      const response = await fetch(`${API_URL}/web/call-remark/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          uniqueid: uniqueId,
          remark: remark,
          is_cloud: isCloudValue,
        }),
      });
      if (response.ok) console.log("Remark saved successfully");
    } catch (error) {
      console.error("Error saving remark:", error);
    } finally {
      setIsSavingRemark(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // ==========================================
  // --- CALL HANDLERS ---
  // ==========================================
const updateCallStatus = async (eventType, statusStr) => {
  if (!uniqueId && !linkedId) return;

  // Yahan hum event aur status set kar rahe hain
  const payload = { event: eventType, data: { status: statusStr } };

  // Yahan hum API ke "uniqueid" field mein apna "linkedId" daal rahe hain
  payload.data.uniqueid = linkedId || uniqueId;

  try {
    await fetch(`${API_URL}/web/update_call_status/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload), // Ye aapka exact wahi payload server ko bhej dega
    });
  } catch (error) {
    console.error("Error in updateCallStatus API:", error);
  }
};

  const handleCallInitiate = async () => {
    if (!dialedValue) return;
    setIsCalling(true);
    try {
      const response = await fetch(`${API_URL}/web/redial/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          extension: myExtension,
          phone_number: dialedValue,
          is_cloud: isCloudValue,
        }),
      });
      if (response.ok) setDialedValue("");
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setIsCalling(false);
    }
  };

  const handleAcceptCall = async () =>
    await updateCallStatus("answered", "ANSWERED");

  const handleHangupCall = async () =>
    await updateCallStatus("rejected", "REJECTED");

  // 🚀 FIXED REJECT LOGIC
  const handleRejectCall = async () => {
    await updateCallStatus("rejected", "REJECTED");
    if (linkedId) {
      handleRejectLocally(linkedId); // This stops the UI from popping up again
    }
  };

  const handleKeyDown = useCallback(
    (event) => {
      if (
        !showSideDialpad ||
        hasActiveCall ||
        isCalling ||
        activeTab !== "DIALPAD"
      )
        return;
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      )
        return;

      const allowedKeys = [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "*",
        "#",
        "+",
      ];
      if (allowedKeys.includes(event.key)) {
        setDialedValue((prev) => (prev.length < 15 ? prev + event.key : prev));
      } else if (event.key === "Backspace") {
        setDialedValue((prev) => prev.slice(0, -1));
      } else if (event.key === "Enter") {
        handleCallInitiate();
      }
    },
    [showSideDialpad, hasActiveCall, isCalling, activeTab],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const keys = [
    { n: "1" },
    { n: "2" },
    { n: "3" },
    { n: "4" },
    { n: "5" },
    { n: "6" },
    { n: "7" },
    { n: "8" },
    { n: "9" },
    { n: "*" },
    { n: "0" },
    { n: "#" },
  ];

  if (!isWidgetVisible) return null;

  return (
    <Box
      ref={constraintsRef}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 99999,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        paddingBottom: "40px",
        paddingLeft: "40px",
        paddingTop: "40px",
        paddingRight: "40px",
      }}
    >
      <AnimatePresence mode="wait">
        <Box
          component={motion.div}
          drag
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={constraintsRef}
          sx={{
            pointerEvents: "auto",
            touchAction: "none",
            cursor: isMinimized ? "grab" : "default",
          }}
        >
          {isMinimized ? (
            /* MINIMIZED STATE */
            <motion.div
              key="minimized-bubble"
              initial={{ scale: 0.5, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              style={{ display: "flex", alignItems: "center", gap: "12px" }}
            >
              <AnimatePresence>
                {isAnswered && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, x: 20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.8, x: 20 }}
                  >
                    <Paper
                      elevation={6}
                      sx={{
                        px: 2,
                        py: 0.8,
                        borderRadius: "24px",
                        bgcolor: "rgba(15, 23, 42, 0.85)",
                        backdropFilter: "blur(10px)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <Box
                        component={motion.div}
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#00D2A0",
                          boxShadow: "0 0 8px #00D2A0",
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          fontSize: "1rem",
                          letterSpacing: 1.5,
                        }}
                      >
                        {formatTime(callDuration)}
                      </Typography>
                    </Paper>
                  </motion.div>
                )}
              </AnimatePresence>

              <Box sx={{ position: "relative" }}>
                {isRinging && (
                  <Box
                    component={motion.div}
                    animate={{ scale: [1, 1.8], opacity: [0.6, 0] }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: "easeOut",
                    }}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: themeColor,
                      borderRadius: "50%",
                      zIndex: -1,
                    }}
                  />
                )}
                <Fab
                  onClick={() => setIsMinimized(false)}
                  sx={{
                    bgcolor: themeColor,
                    color: "white",
                    width: 55,
                    height: 55,
                    boxShadow: isRinging
                      ? `0 0 25px ${themeColor}88`
                      : "0 10px 20px rgba(0,0,0,0.2)",
                    "&:hover": {
                      bgcolor: themeColor,
                      filter: "brightness(0.9)",
                    },
                  }}
                >
                  {isRinging && isIncomingCallFlow ? (
                    <RingVolumeIcon sx={{ fontSize: 24 }} />
                  ) : isRinging && isOutgoingCallFlow ? (
                    <PhoneCallbackIcon sx={{ fontSize: 24 }} />
                  ) : isCompleted ? (
                    <CallEndIcon sx={{ fontSize: 24 }} />
                  ) : isAnswered ? (
                    <PhoneInTalkIcon sx={{ fontSize: 24 }} />
                  ) : (
                    <DialpadIcon sx={{ fontSize: 24 }} />
                  )}
                </Fab>
              </Box>
            </motion.div>
          ) : (
            /* EXPANDED CARD STATE */
            <motion.div
              key="expanded-card"
              initial={{ scale: 0.8, opacity: 0, originX: 1, originY: 1 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 350, damping: 25 }}
            >
              <Paper
                component={motion.div}
                layout
                elevation={24}
                sx={{
                  width: hasActiveCall ? "340px" : "330px",
                  borderRadius: "24px",
                  overflow: "hidden",
                  background: "#ffffff",
                  border: `1px solid ${themeColor}33`,
                  boxShadow: `0 24px 64px ${themeColor}22`,
                  transition: "width 0.3s",
                }}
              >
                {/* HEADER */}
                <Box
                  component={motion.div}
                  layout
                  sx={{
                    bgcolor: hasActiveCall ? `${themeColor}15` : "#347B89",
                    px: 1.5,
                    py: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: hasActiveCall ? themeColor : "#fff",
                    cursor: "grab",
                    borderBottom: hasActiveCall
                      ? `1px solid ${themeColor}33`
                      : "none",
                    transition: "background-color 0.3s",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {!hasActiveCall && activeTab === "DIALPAD" ? (
                      <IconButton
                        size="small"
                        onClick={() => setActiveTab("RECENTS")}
                        sx={{ color: "inherit", padding: "4px" }}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <DragIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: 1,
                        fontSize: "0.7rem",
                      }}
                    >
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={safeStatus + activeTab}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                        >
                          {hasActiveCall
                            ? isCompleted
                              ? "CALL ENDED"
                              : isRinging && isIncomingCallFlow
                                ? "INCOMING CALL"
                                : isRinging && isOutgoingCallFlow
                                  ? "OUTGOING CALL"
                                  : "ACTIVE CALL"
                            : activeTab === "RECENTS"
                              ? "RECENT CALLS"
                              : "DIGITAL DIALER"}
                        </motion.span>
                      </AnimatePresence>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => setIsMinimized(true)}
                      sx={{ color: "inherit", padding: "4px" }}
                    >
                      <MinimizeIcon fontSize="small" />
                    </IconButton>
                    {!hasActiveCall && (
                      <IconButton
                        size="small"
                        onClick={() => setShowSideDialpad(false)}
                        sx={{ color: "inherit", padding: "4px" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Box>

                {/* CONTENT AREA */}
                <Box sx={{ p: 2, position: "relative" }}>
                  <AnimatePresence mode="wait">
                    {/* 1. ACTIVE CALL UI */}
                    {hasActiveCall ? (
                      <motion.div
                        key="active-call-content"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <motion.div
                            animate={isRinging ? { scale: [1, 1.1, 1] } : {}}
                            transition={{ repeat: Infinity, duration: 1.2 }}
                          >
                            <Avatar
                              sx={{
                                width: 50,
                                height: 50,
                                bgcolor: themeColor,
                                boxShadow: `0 8px 24px ${themeColor}66`,
                                transition: "background-color 0.3s",
                              }}
                            >
                              {isCompleted ? (
                                <CallEndIcon />
                              ) : isRinging && isIncomingCallFlow ? (
                                <RingVolumeIcon />
                              ) : isRinging && isOutgoingCallFlow ? (
                                <PhoneCallbackIcon />
                              ) : (
                                <PhoneInTalkIcon />
                              )}
                            </Avatar>
                          </motion.div>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 900,
                                color: "#1e293b",
                                lineHeight: 1.2,
                                fontSize: "1.1rem",
                              }}
                            >
                              {displayNumber || "Unknown Number"}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: themeColor,
                                fontWeight: 800,
                                mt: 0.2,
                                fontSize: "0.8rem",
                                transition: "color 0.3s",
                              }}
                            >
                              {isCompleted
                                ? "Call Disconnected"
                                : isRinging && isIncomingCallFlow
                                  ? "Incoming..."
                                  : isRinging && isOutgoingCallFlow
                                    ? "Ringing..."
                                    : "On Call"}
                            </Typography>
                            {(isAnswered || isCompleted) && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  color: "#64748b",
                                  mt: 0.2,
                                  fontFamily: "monospace",
                                }}
                              >
                                {formatTime(callDuration)}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        {/* CALL REMARK */}
                        {isAnswered && (
                          <Box
                            sx={{
                              mt: 2,
                              pt: 1.5,
                              borderTop: "1px dashed #e2e8f0",
                            }}
                          >
                            {isLoadingRemark ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="center"
                              >
                                <CircularProgress size={16} />
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Loading Remark...
                                </Typography>
                              </Stack>
                            ) : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  placeholder="Type call remark here..."
                                  value={remark}
                                  onChange={(e) => setRemark(e.target.value)}
                                  disabled={isSavingRemark}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: "36px",
                                      fontSize: "0.85rem",
                                      borderRadius: "8px",
                                    },
                                  }}
                                />
                                <IconButton
                                  onClick={handleSaveRemark}
                                  disabled={isSavingRemark || !remark.trim()}
                                  sx={{
                                    bgcolor: "#347B89",
                                    color: "#fff",
                                    borderRadius: "8px",
                                    width: "36px",
                                    height: "36px",
                                    "&:hover": { bgcolor: "#2A636E" },
                                    "&.Mui-disabled": {
                                      bgcolor: "#e2e8f0",
                                      color: "#94a3b8",
                                    },
                                  }}
                                >
                                  {isSavingRemark ? (
                                    <CircularProgress
                                      size={18}
                                      color="inherit"
                                    />
                                  ) : (
                                    <SaveIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Stack>
                            )}
                          </Box>
                        )}

                        {/* CALL ACTION BUTTONS */}
                        <Box sx={{ mt: 2.5 }}>
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={
                                isRinging
                                  ? "ringing-btns"
                                  : isCompleted
                                    ? "completed-btns"
                                    : "answered-btns"
                              }
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                            >
                              {isCompleted ? (
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  disabled
                                  sx={{
                                    borderRadius: "10px",
                                    py: 0.8,
                                    fontWeight: "bold",
                                  }}
                                >
                                  Call Ended
                                </Button>
                              ) : isRinging ? (
                                isIncomingCallFlow ? (
                                  <Stack direction="row" spacing={1.5}>
                                    <motion.div
                                      style={{ flex: 1 }}
                                      whileHover={{ scale: 1.03 }}
                                      whileTap={{ scale: 0.95 }}
                                    >
                                      <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleRejectCall}
                                        startIcon={<CallEndIcon />}
                                        sx={{
                                          bgcolor: "#ef4444",
                                          color: "#fff",
                                          borderRadius: "10px",
                                          py: 0.8,
                                          fontWeight: "bold",
                                          "&:hover": { bgcolor: "#dc2626" },
                                        }}
                                      >
                                        Reject
                                      </Button>
                                    </motion.div>
                                    <motion.div
                                      style={{ flex: 1 }}
                                      whileHover={{ scale: 1.03 }}
                                      whileTap={{ scale: 0.95 }}
                                    >
                                      <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleAcceptCall}
                                        startIcon={<CallIcon />}
                                        sx={{
                                          bgcolor: "#10b981",
                                          color: "#fff",
                                          borderRadius: "10px",
                                          py: 0.8,
                                          fontWeight: "bold",
                                          "&:hover": { bgcolor: "#059669" },
                                        }}
                                      >
                                        Answer
                                      </Button>
                                    </motion.div>
                                  </Stack>
                                ) : (
                                  <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <Button
                                      fullWidth
                                      variant="contained"
                                      onClick={handleHangupCall}
                                      startIcon={<CallEndIcon />}
                                      sx={{
                                        bgcolor: "#ef4444",
                                        color: "#fff",
                                        borderRadius: "10px",
                                        py: 0.8,
                                        fontWeight: "bold",
                                        "&:hover": { bgcolor: "#dc2626" },
                                      }}
                                    >
                                      Cancel Call
                                    </Button>
                                  </motion.div>
                                )
                              ) : (
                                <Stack direction="row" spacing={1.5}>
                                  <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.9 }}
                                  >
                                    <Button
                                      variant="contained"
                                      onClick={handleHangupCall}
                                      sx={{
                                        minWidth: "56px",
                                        height: "100%",
                                        bgcolor: "#ef4444",
                                        color: "#fff",
                                        borderRadius: "10px",
                                        "&:hover": { bgcolor: "#dc2626" },
                                      }}
                                    >
                                      <CallEndIcon />
                                    </Button>
                                  </motion.div>
                                  <motion.div
                                    style={{ flex: 1 }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <Button
                                      fullWidth
                                      variant="contained"
                                      startIcon={<PersonIcon />}
                                      onClick={() =>
                                        navigate("/addservice", {
                                          state: {
                                            incomingNumber: displayNumber,
                                          },
                                        })
                                      }
                                      sx={{
                                        borderRadius: "10px",
                                        bgcolor: "#0f172a",
                                        color: "#fff",
                                        py: 0.8,
                                        textTransform: "none",
                                        fontWeight: "bold",
                                        fontSize: "0.9rem",
                                        "&:hover": { bgcolor: "#000" },
                                      }}
                                    >
                                      Add Service Record
                                    </Button>
                                  </motion.div>
                                </Stack>
                              )}
                            </motion.div>
                          </AnimatePresence>
                        </Box>
                      </motion.div>
                    ) : activeTab === "RECENTS" ? (
                      /* 2. RECENT CALLS UI */
                      <motion.div
                        key="recent-calls-content"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {/* Search & Filter Header */}
                        <Box sx={{ mb: 1.5 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Search number..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <SearchIcon
                                  sx={{
                                    color: "text.secondary",
                                    mr: 1,
                                    fontSize: 20,
                                  }}
                                />
                              ),
                              endAdornment: (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.5}
                                >
                                  {searchQuery && (
                                    <IconButton
                                      size="small"
                                      onClick={() => setSearchQuery("")}
                                      sx={{ color: "text.secondary" }}
                                    >
                                      <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setShowAdvancedFilters(
                                        !showAdvancedFilters,
                                      )
                                    }
                                    sx={{
                                      color: showAdvancedFilters
                                        ? "#347B89"
                                        : "text.secondary",
                                      bgcolor: showAdvancedFilters
                                        ? "rgba(52, 123, 137, 0.1)"
                                        : "transparent",
                                    }}
                                  >
                                    <FilterListIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </Stack>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-root": {
                                borderRadius: "12px",
                                fontSize: "0.85rem",
                                height: "40px",
                              },
                            }}
                          />
                        </Box>

                        {/* Collapsible Date Filters */}
                        <AnimatePresence>
                          {showAdvancedFilters && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              style={{ overflow: "hidden" }}
                            >
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{
                                  mb: 2,
                                  p: 1,
                                  bgcolor: "#f8fafc",
                                  borderRadius: "12px",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                <TextField
                                  fullWidth
                                  type={dateRange.from ? "date" : "text"}
                                  onFocus={(e) => (e.target.type = "date")}
                                  onBlur={(e) => {
                                    if (!dateRange.from) e.target.type = "text";
                                  }}
                                  size="small"
                                  label="From"
                                  value={dateRange.from}
                                  onChange={(e) =>
                                    setDateRange({
                                      ...dateRange,
                                      from: e.target.value,
                                    })
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      bgcolor: "#ffffff",
                                      fontSize: "0.85rem",
                                      "& fieldset": { borderColor: "#e2e8f0" },
                                      "&:hover fieldset": {
                                        borderColor: "#cbd5e1",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#347B89",
                                        borderWidth: "1px",
                                      },
                                    },
                                    "& .MuiInputLabel-root": {
                                      fontSize: "0.85rem",
                                      color: "#64748b",
                                    },
                                  }}
                                />
                                <TextField
                                  fullWidth
                                  type={dateRange.to ? "date" : "text"}
                                  onFocus={(e) => (e.target.type = "date")}
                                  onBlur={(e) => {
                                    if (!dateRange.to) e.target.type = "text";
                                  }}
                                  size="small"
                                  label="To"
                                  value={dateRange.to}
                                  onChange={(e) =>
                                    setDateRange({
                                      ...dateRange,
                                      to: e.target.value,
                                    })
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      bgcolor: "#ffffff",
                                      fontSize: "0.85rem",
                                      "& fieldset": { borderColor: "#e2e8f0" },
                                      "&:hover fieldset": {
                                        borderColor: "#cbd5e1",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#347B89",
                                        borderWidth: "1px",
                                      },
                                    },
                                    "& .MuiInputLabel-root": {
                                      fontSize: "0.85rem",
                                      color: "#64748b",
                                    },
                                  }}
                                />
                                {(dateRange.from || dateRange.to) && (
                                  <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setDateRange({ from: "", to: "" })
                                      }
                                      sx={{
                                        color: "#ef4444",
                                        bgcolor: "#fef2f2",
                                        height: "36px",
                                        width: "36px",
                                        borderRadius: "8px",
                                        "&:hover": { bgcolor: "#fee2e2" },
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </motion.div>
                                )}
                              </Stack>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Filter Tabs */}
                        <Stack
                          direction="row"
                          spacing={1.5}
                          sx={{
                            mb: 2,
                            overflowX: "auto",
                            pt: 1,
                            pb: 1,
                            px: 0.5,
                            "&::-webkit-scrollbar": { display: "none" },
                          }}
                        >
                          {[
                            { id: "", label: "All", count: counts.total },
                            {
                              id: "missed",
                              label: "Missed",
                              count: counts.missed,
                            },
                            {
                              id: "incoming",
                              label: "Incoming",
                              count: counts.incoming,
                            },
                            {
                              id: "outgoing",
                              label: "Outgoing",
                              count: counts.outgoing,
                            },
                          ].map((tab) => (
                            <Button
                              key={tab.id}
                              size="small"
                              variant={
                                callFilter === tab.id ? "contained" : "outlined"
                              }
                              onClick={() => setCallFilter(tab.id)}
                              sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                minWidth: "auto",
                                px: 2,
                                py: 0.6,
                                bgcolor:
                                  callFilter === tab.id
                                    ? "#347B89"
                                    : "transparent",
                                color:
                                  callFilter === tab.id ? "#fff" : "#475569",
                                borderColor:
                                  callFilter === tab.id ? "#347B89" : "#cbd5e1",
                                "&:hover": {
                                  bgcolor:
                                    callFilter === tab.id
                                      ? "#2A636E"
                                      : "#f1f5f9",
                                  borderColor: "#94a3b8",
                                },
                                whiteSpace: "nowrap",
                                overflow: "visible",
                              }}
                            >
                              <Badge
                                badgeContent={
                                  <AnimatedCount target={tab.count} />
                                }
                                sx={{
                                  "& .MuiBadge-badge": {
                                    bgcolor: "#ef4444",
                                    color: "#fff",
                                    fontWeight: 800,
                                    fontSize: "0.65rem",
                                    padding: "0 4px",
                                    height: "18px",
                                    minWidth: "18px",
                                    right: -12,
                                    top: -2,
                                  },
                                  pr: 1.5,
                                }}
                              >
                                {tab.label}
                              </Badge>
                            </Button>
                          ))}
                        </Stack>

                        {/* Call List Container */}
                        <Box
                          sx={{
                            maxHeight: "260px",
                            overflowY: "auto",
                            pr: 1,
                            "&::-webkit-scrollbar": { width: "4px" },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#cbd5e1",
                              borderRadius: "4px",
                            },
                          }}
                        >
                          {callsLoading ? (
                            Array.from(new Array(4)).map((_, i) => (
                              <Stack
                                key={i}
                                direction="row"
                                spacing={1.5}
                                sx={{ mb: 2, alignItems: "center" }}
                              >
                                <Skeleton
                                  variant="circular"
                                  width={36}
                                  height={36}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Skeleton
                                    variant="text"
                                    width="60%"
                                    sx={{ fontSize: "1rem" }}
                                  />
                                  <Skeleton
                                    variant="text"
                                    width="40%"
                                    sx={{ fontSize: "0.8rem" }}
                                  />
                                </Box>
                                <Skeleton
                                  variant="circular"
                                  width={28}
                                  height={28}
                                />
                              </Stack>
                            ))
                          ) : filteredGroups.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              align="center"
                              sx={{ py: 4 }}
                            >
                              No recent calls found.
                            </Typography>
                          ) : (
                            filteredGroups.map((groupData, groupIndex) => (
                              <Box key={groupIndex} sx={{ mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 800,
                                    color: "#94a3b8",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    mb: 1,
                                    display: "block",
                                    pl: 0.5,
                                    textAlign: "left",
                                  }}
                                >
                                  {groupData.group}
                                </Typography>

                                {groupData.calls.map((call, callIndex) => {
                                  let callType = "incoming";
                                  let tooltipText = "Incoming Call";

                                  if (call.direction === "OUTBOUND") {
                                    callType = "outgoing";
                                    tooltipText = "Outgoing Call";
                                  } else if (
                                    call.direction === "INBOUND" &&
                                    (call.status === "MISSED" ||
                                      call.status === "REJECTED" ||
                                      call.status === "RINGING")
                                  ) {
                                    callType = "missed";
                                    tooltipText =
                                      call.status === "REJECTED"
                                        ? "Rejected Call"
                                        : "Missed Call";
                                  }

                                  const phoneNumber =
                                    call.direction === "OUTBOUND"
                                      ? call.dst
                                      : call.src;

                                  return (
                                    <Box
                                      key={call.uniqueid || callIndex}
                                      sx={{ mb: 1.5 }}
                                    >
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                      >
                                        <Tooltip
                                          title={tooltipText}
                                          placement="top"
                                          arrow
                                        >
                                          <Avatar
                                            sx={{
                                              width: 36,
                                              height: 36,
                                              bgcolor:
                                                callType === "missed"
                                                  ? "#fef2f2"
                                                  : "#f0fdf4",
                                              color:
                                                callType === "missed"
                                                  ? "#ef4444"
                                                  : callType === "outgoing"
                                                    ? "#3498db"
                                                    : "#10b981",
                                            }}
                                          >
                                            {callType === "missed" ? (
                                              <CallMissedIcon fontSize="small" />
                                            ) : callType === "outgoing" ? (
                                              <CallMadeIcon fontSize="small" />
                                            ) : (
                                              <CallReceivedIcon fontSize="small" />
                                            )}
                                          </Avatar>
                                        </Tooltip>

                                        <Box
                                          sx={{
                                            flexGrow: 1,
                                            textAlign: "left",
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            sx={{
                                              fontWeight: 700,
                                              color:
                                                callType === "missed"
                                                  ? "#ef4444"
                                                  : "#1e293b",
                                              fontSize: "0.9rem",
                                              textAlign: "left",
                                            }}
                                          >
                                            {cleanPhoneNumber(phoneNumber) ||
                                              "Unknown"}
                                          </Typography>
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.5}
                                            sx={{
                                              justifyContent: "flex-start",
                                            }}
                                          >
                                            <AccessTimeIcon
                                              sx={{
                                                fontSize: 12,
                                                color: "#94a3b8",
                                              }}
                                            />
                                            <Typography
                                              variant="caption"
                                              sx={{ color: "#94a3b8" }}
                                            >
                                              {call.call_time || "Recently"}
                                            </Typography>
                                          </Stack>
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setDialedValue(
                                              cleanPhoneNumber(phoneNumber),
                                            );
                                            setActiveTab("DIALPAD");
                                          }}
                                          sx={{
                                            color: "#347B89",
                                            bgcolor: "#f8fafc",
                                            "&:hover": { bgcolor: "#f1f5f9" },
                                          }}
                                        >
                                          <CallIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                      {callIndex !==
                                        groupData.calls.length - 1 && (
                                        <Divider sx={{ mt: 1.5 }} />
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            ))
                          )}
                        </Box>

                        {/* Switch to Dialpad */}
                        <Fab
                          size="medium"
                          onClick={() => setActiveTab("DIALPAD")}
                          sx={{
                            position: "absolute",
                            bottom: 16,
                            right: 16,
                            bgcolor: "#347B89",
                            color: "#fff",
                            boxShadow: "0 4px 14px rgba(52,123,137,0.4)",
                            "&:hover": { bgcolor: "#2A636E" },
                          }}
                        >
                          <DialpadIcon />
                        </Fab>
                      </motion.div>
                    ) : (
                      /* 3. ORIGINAL DIALPAD UI */
                      <motion.div
                        key="dialer-content"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Box sx={{ mb: 2, textAlign: "center" }}>
                          <InputBase
                            value={dialedValue}
                            onChange={(e) =>
                              setDialedValue(
                                e.target.value
                                  .replace(/[^0-9*#+]/g, "")
                                  .slice(0, 15),
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleCallInitiate();
                            }}
                            placeholder="Dial..."
                            inputProps={{
                              style: {
                                textAlign: "center",
                                fontWeight: 700,
                                letterSpacing: 2,
                                fontSize: "1.4rem",
                                color: "#1e293b",
                              },
                            }}
                            sx={{ width: "100%", height: "35px" }}
                          />
                        </Box>
                        <Grid container spacing={1}>
                          {keys.map((key) => (
                            <Grid item xs={4} key={key.n}>
                              <motion.div whileTap={{ scale: 0.9 }}>
                                <Button
                                  fullWidth
                                  onClick={() =>
                                    setDialedValue((prev) =>
                                      prev.length < 15 ? prev + key.n : prev,
                                    )
                                  }
                                  sx={{
                                    height: "46px",
                                    borderRadius: "12px",
                                    bgcolor: "#f8fafc",
                                    color: "#334155",
                                    fontSize: "1.1rem",
                                    fontWeight: "bold",
                                    boxShadow: "inset 0 0 0 1px #e2e8f0",
                                    "&:hover": {
                                      bgcolor: "#f1f5f9",
                                      boxShadow: "inset 0 0 0 1px #94a3b8",
                                    },
                                  }}
                                >
                                  {key.n}
                                </Button>
                              </motion.div>
                            </Grid>
                          ))}
                        </Grid>
                        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                          <motion.div whileTap={{ scale: 0.9 }}>
                            <IconButton
                              onClick={() =>
                                setDialedValue((prev) => prev.slice(0, -1))
                              }
                              disabled={isCalling || !dialedValue}
                              sx={{
                                width: "46px",
                                height: "46px",
                                bgcolor: "#fef2f2",
                                color: "#ef4444",
                                borderRadius: "12px",
                                "&:hover": { bgcolor: "#fee2e2" },
                              }}
                            >
                              <BackspaceIcon fontSize="small" />
                            </IconButton>
                          </motion.div>
                          <motion.div
                            style={{ flex: 1 }}
                            whileHover={
                              !isCalling && dialedValue ? { scale: 1.02 } : {}
                            }
                            whileTap={
                              !isCalling && dialedValue ? { scale: 0.95 } : {}
                            }
                          >
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={handleCallInitiate}
                              disabled={isCalling || !dialedValue}
                              startIcon={
                                isCalling ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <CallIcon />
                                )
                              }
                              sx={{
                                height: "46px",
                                borderRadius: "12px",
                                bgcolor: "#347B89",
                                fontWeight: 700,
                                fontSize: "1rem",
                                boxShadow: "0 8px 20px rgba(52, 123, 137, 0.3)",
                                "&:hover": { bgcolor: "#2A636E" },
                              }}
                            >
                              {isCalling ? "CALLING..." : "CALL"}
                            </Button>
                          </motion.div>
                        </Stack>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </Paper>
            </motion.div>
          )}
        </Box>
      </AnimatePresence>
    </Box>
  );
}
